import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import Tabs from "@/components/base/Tabs";
import StatCard from "@/components/base/StatCard";
import { Select, Textarea } from "@/components/base/Field";
import { EmptyState, ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSchoolYear } from "@/contexts/SchoolYearContext";
import { useAsync } from "@/hooks/useAsync";
import { getClasses } from "@/services/academicService";
import {
  formatRank,
  formatScore,
  getAnnualResults,
  getClassReport,
  getEvaluations,
  getPeriods,
  reportTitle,
  saveRemark,
  type AnnualResults,
  type ClassReport,
  type Evaluation,
  type StudentResult,
} from "@/services/gradeService";
import { getSchoolInfo } from "@/services/schoolService";
import { exportReportCardsPdf } from "@/utils/reportCardPdf";
import { exportMeritListPdf, MERIT_OPTION_LABELS, type MeritListOptions } from "@/utils/meritListPdf";

const MERIT_KEY = "waitechedu.meritListOptions";
const DEFAULT_MERIT: MeritListOptions = {
  matricule: true,
  gender: false,
  birthDate: false,
  totalPoints: false,
  subjects: false,
  remark: false,
  signature: false,
};

/** Colonnes cochées la dernière fois (préférence de ce navigateur). */
function savedMeritOptions(): MeritListOptions {
  try {
    return { ...DEFAULT_MERIT, ...(JSON.parse(localStorage.getItem(MERIT_KEY) ?? "{}") as Partial<MeritListOptions>) };
  } catch {
    return DEFAULT_MERIT;
  }
}

const tabs = [
  { id: "period", label: "Bulletins et classements", icon: "ri-file-chart-line" },
  { id: "annual", label: "Résultats de l'année", icon: "ri-medal-line" },
];

async function loadPage(schoolYear: string) {
  const [periods, classes] = await Promise.all([getPeriods(schoolYear), getClasses()]);
  return { periods, classes };
}

/** Bulletins : moyennes, rangs et mentions d'une classe, appréciations, impression ; résultats de fin d'année. */
export default function ReportCardsPage() {
  const { notify } = useToast();
  const { can } = useAuth();
  const { consulted } = useSchoolYear();
  const { data, loading, error, reload } = useAsync(() => loadPage(consulted), [consulted]);
  const [tab, setTab] = useState("period");
  const [periodId, setPeriodId] = useState("");
  const [classId, setClassId] = useState("");
  const [report, setReport] = useState<ClassReport | null>(null);
  const [annual, setAnnual] = useState<AnnualResults | null>(null);
  const [busy, setBusy] = useState(false);
  const [remarkOf, setRemarkOf] = useState<StudentResult | null>(null);
  const [remark, setRemark] = useState("");
  const [printing, setPrinting] = useState(false);
  /** Bulletin d'une évaluation ; vide : bulletin du trimestre (toutes ses évaluations). */
  const [evaluationId, setEvaluationId] = useState("");
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [meritOpen, setMeritOpen] = useState(false);
  const [meritOptions, setMeritOptions] = useState<MeritListOptions>(savedMeritOptions);

  useEffect(() => {
    setEvaluationId("");
    setEvaluations([]);
    if (!periodId || !classId) return;
    getEvaluations(periodId, classId)
      .then(setEvaluations)
      .catch(() => setEvaluations([]));
  }, [periodId, classId]);

  useEffect(() => {
    if (!data) return;
    setPeriodId((prev) => (data.periods.some((p) => p.id === prev) ? prev : (data.periods[0]?.id ?? "")));
    setClassId((prev) => (data.classes.some((c) => c.id === prev) ? prev : (data.classes[0]?.id ?? "")));
  }, [data]);

  const loadResults = useMemo(
    () => async () => {
      if (!classId) return;
      setBusy(true);
      try {
        if (tab === "period") {
          if (periodId) setReport(await getClassReport(periodId, classId, evaluationId || undefined));
        } else {
          setAnnual(await getAnnualResults(classId, consulted));
        }
      } catch (err) {
        notify(err instanceof Error ? err.message : "Chargement impossible.", "error");
      } finally {
        setBusy(false);
      }
    },
    [tab, periodId, classId, evaluationId, consulted, notify],
  );
  useEffect(() => {
    void loadResults();
  }, [loadResults]);

  if (loading && !data) return <LoadingState label="Chargement des bulletins..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  if (data.periods.length === 0) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Bulletins" subtitle={`Année ${consulted}`} icon="ri-file-chart-line" />
        <Card>
          <EmptyState
            icon="ri-calendar-schedule-line"
            title="Les périodes de notes ne sont pas encore créées"
            message="Découpez l'année en trimestres ou en semestres dans Paramètres › Notes et périodes."
          />
        </Card>
      </div>
    );
  }

  const print = async (enrollmentIds?: string[]) => {
    if (!report) return;
    setPrinting(true);
    try {
      await exportReportCardsPdf(report, await getSchoolInfo(), enrollmentIds);
    } catch {
      notify("Impression impossible.", "error");
    } finally {
      setPrinting(false);
    }
  };

  const openRemark = (s: StudentResult) => {
    setRemarkOf(s);
    setRemark(s.remark ?? "");
  };
  const submitRemark = async () => {
    if (!remarkOf || !report) return;
    try {
      await saveRemark(report.period.id, remarkOf.enrollmentId, remark);
      notify("Appréciation enregistrée.");
      setRemarkOf(null);
      void loadResults();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    }
  };

  const ranked = (list: StudentResult[]) =>
    [...list].sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999) || a.lastName.localeCompare(b.lastName, "fr"));
  const period = data.periods.find((p) => p.id === periodId);
  const canRemark = can("report-cards:update") && !!period && !period.isLocked && report?.scope === "PERIOD";

  const printMerit = async () => {
    if (!report) return;
    try {
      localStorage.setItem(MERIT_KEY, JSON.stringify(meritOptions));
    } catch {
      /* préférence non mémorisée : sans conséquence */
    }
    setPrinting(true);
    try {
      await exportMeritListPdf(report, await getSchoolInfo(), meritOptions);
      setMeritOpen(false);
    } catch {
      notify("Impression impossible.", "error");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Bulletins"
        subtitle={`Résultats et bulletins — année ${consulted}`}
        icon="ri-file-chart-line"
        actions={
          can("grades:read") ? (
            <Link to="/dashboard/grades">
              <Button variant="outline" icon="ri-edit-box-line">
                Saisir les notes
              </Button>
            </Link>
          ) : undefined
        }
      />

      <div className="mb-4">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <Card className="mb-4">
        <div className={`grid grid-cols-1 gap-3 ${tab === "period" ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          {tab === "period" && (
            <Select label="Période" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
              {data.periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isLocked ? " (verrouillé)" : ""}
                </option>
              ))}
            </Select>
          )}
          <Select label="Classe" value={classId} onChange={(e) => setClassId(e.target.value)}>
            {data.classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          {tab === "period" && (
            <Select label="Bulletin de" value={evaluationId} onChange={(e) => setEvaluationId(e.target.value)}>
              <option value="">
                Tout le {period?.name ?? "trimestre"} ({evaluations.length} évaluation{evaluations.length > 1 ? "s" : ""})
              </option>
              {evaluations.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </Select>
          )}
        </div>
        {tab === "period" && period && !period.isLocked && (
          <p className="mt-3 text-xs text-foreground-500">
            La période est encore ouverte : les moyennes peuvent changer. Verrouillez-la (Paramètres › Notes et périodes)
            avant d'imprimer les bulletins définitifs.
          </p>
        )}
      </Card>

      {busy && !report && !annual ? (
        <LoadingState label="Calcul des moyennes..." />
      ) : tab === "period" ? (
        report && (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Élèves" value={String(report.stats.count)} icon="ri-group-line" hint={`${report.stats.graded} avec une moyenne`} />
              <StatCard label="Moyenne de la classe" value={report.stats.average === null ? "—" : `${formatScore(report.stats.average)} / ${report.gradeScale}`} icon="ri-bar-chart-line" />
              <StatCard label="Plus forte moyenne" value={formatScore(report.stats.highest)} icon="ri-arrow-up-line" tone="secondary" hint={`Plus faible : ${formatScore(report.stats.lowest)}`} />
              <StatCard
                label="Au-dessus de la moyenne"
                value={`${report.stats.passed} / ${report.stats.graded}`}
                icon="ri-checkbox-circle-line"
                tone="accent"
                hint={`Seuil : ${formatScore(report.passingAverage)}`}
              />
            </div>

            <Card padded={false}>
              <div className="flex flex-col gap-2 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold font-heading text-foreground-950">
                    {report.className} — {reportTitle(report)}
                  </h3>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {report.scope === "PERIOD"
                      ? `Note du trimestre dans chaque matière = moyenne des ${report.evaluations.length} évaluation${report.evaluations.length > 1 ? "s" : ""} ; moyenne générale avec les coefficients.`
                      : "Notes de cette seule évaluation ; moyenne générale avec les coefficients."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" icon="ri-list-ordered" onClick={() => setMeritOpen(true)} disabled={printing || report.students.length === 0}>
                    Liste par ordre de mérite
                  </Button>
                  <Button icon="ri-printer-line" onClick={() => print()} disabled={printing || report.students.length === 0}>
                    {printing ? "Préparation..." : "Imprimer les bulletins"}
                  </Button>
                </div>
              </div>
              {report.students.length === 0 ? (
                <div className="p-4">
                  <EmptyState icon="ri-user-3-line" title="Aucun élève" message="Aucun élève n'est inscrit dans cette classe cette année." />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: 640 + report.subjects.length * 90 }}>
                    <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Rang</th>
                        <th className="px-3 py-2.5 font-medium">Élève</th>
                        {report.subjects.map((s) => (
                          <th key={s.subjectId} className="px-3 py-2.5 font-medium text-right" title={`Coefficient ${s.coefficient}`}>
                            {s.name}
                            <span className="block normal-case tracking-normal text-foreground-400">coef. {s.coefficient}</span>
                          </th>
                        ))}
                        <th className="px-3 py-2.5 font-medium text-right">Moyenne</th>
                        <th className="px-3 py-2.5 font-medium">Mention</th>
                        <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked(report.students).map((s) => {
                        const bySubject = new Map(s.subjects.map((x) => [x.subjectId, x]));
                        const below = s.average !== null && s.average < report.passingAverage;
                        return (
                          <tr key={s.enrollmentId} className="border-t border-background-200">
                            <td className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">
                              {formatRank(s.rank, s.rankTied, s.gender)}
                            </td>
                            <td className="px-3 py-3">
                              <p className="font-medium text-foreground-900">
                                {s.lastName.toUpperCase()} {s.firstName}
                              </p>
                              <p className="text-xs text-foreground-500">
                                {s.matricule}
                                {s.remark ? " · appréciation saisie" : ""}
                              </p>
                            </td>
                            {report.subjects.map((subject) => (
                              <td key={subject.subjectId} className="px-3 py-3 text-right tabular-nums">
                                {formatScore(bySubject.get(subject.subjectId)?.average ?? null)}
                              </td>
                            ))}
                            <td className={`px-3 py-3 text-right font-semibold tabular-nums ${below ? "text-accent-700" : "text-foreground-900"}`}>
                              {formatScore(s.average)}
                            </td>
                            <td className="px-3 py-3">{s.mention ? <Badge tone={below ? "danger" : "success"}>{s.mention}</Badge> : "—"}</td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1.5">
                                {canRemark && (
                                  <Button size="sm" variant="outline" icon="ri-chat-quote-line" onClick={() => openRemark(s)}>
                                    Appréciation
                                  </Button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => print([s.enrollmentId])}
                                  disabled={printing}
                                  className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 cursor-pointer disabled:opacity-50"
                                  aria-label={`Imprimer le bulletin de ${s.firstName} ${s.lastName}`}
                                  title="Imprimer son bulletin"
                                >
                                  <i className="ri-printer-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )
      ) : (
        annual && (
          <Card padded={false}>
            <div className="flex flex-col gap-2 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold font-heading text-foreground-950">
                  {annual.className} — année {annual.schoolYear}
                </h3>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Moyenne annuelle = moyenne des périodes notées · admis à partir de {formatScore(annual.passingAverage)} / {annual.gradeScale}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge tone="success" icon="ri-check-line">
                  {annual.stats.admitted} admis
                </Badge>
                <Badge tone="danger" icon="ri-repeat-line">
                  {annual.stats.repeating} redoublent
                </Badge>
                {annual.final ? (
                  <Badge tone="secondary" icon="ri-lock-2-line">
                    Décisions définitives
                  </Badge>
                ) : (
                  <Badge tone="neutral" icon="ri-time-line">
                    Provisoire (périodes non verrouillées)
                  </Badge>
                )}
              </div>
            </div>
            {annual.students.length === 0 ? (
              <div className="p-4">
                <EmptyState icon="ri-user-3-line" title="Aucun élève" message="Aucun élève n'est inscrit dans cette classe cette année." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                    <tr>
                      <th className="px-4 py-2.5 font-medium">Rang</th>
                      <th className="px-3 py-2.5 font-medium">Élève</th>
                      {annual.periods.map((p) => (
                        <th key={p.id} className="px-3 py-2.5 font-medium text-right">
                          {p.name}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 font-medium text-right">Moyenne annuelle</th>
                      <th className="px-3 py-2.5 font-medium">Mention</th>
                      <th className="px-4 py-2.5 font-medium">Décision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...annual.students]
                      .sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999) || a.lastName.localeCompare(b.lastName, "fr"))
                      .map((s) => (
                        <tr key={s.enrollmentId} className="border-t border-background-200">
                          <td className="px-4 py-3 font-semibold whitespace-nowrap">{formatRank(s.rank, s.rankTied)}</td>
                          <td className="px-3 py-3">
                            <span className="font-medium text-foreground-900">{s.lastName.toUpperCase()}</span> {s.firstName}
                            <span className="ml-2 text-xs text-foreground-400">{s.matricule}</span>
                          </td>
                          {s.periodAverages.map((v, i) => (
                            <td key={annual.periods[i]?.id ?? i} className="px-3 py-3 text-right tabular-nums">
                              {formatScore(v)}
                            </td>
                          ))}
                          <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatScore(s.average)}</td>
                          <td className="px-3 py-3">{s.mention ?? "—"}</td>
                          <td className="px-4 py-3">
                            {s.decision === "ADMIS" ? (
                              <Badge tone="success" icon="ri-arrow-right-up-line">
                                Admis
                              </Badge>
                            ) : s.decision === "REDOUBLE" ? (
                              <Badge tone="danger" icon="ri-repeat-line">
                                Redouble
                              </Badge>
                            ) : (
                              <span className="text-xs text-foreground-400">Pas de notes</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )
      )}

      <Modal
        open={meritOpen}
        onClose={() => setMeritOpen(false)}
        title="Liste par ordre de mérite"
        subtitle={report ? `${report.className} — ${reportTitle(report)}` : undefined}
        icon="ri-list-ordered"
        footer={
          <>
            <Button variant="outline" onClick={() => setMeritOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-printer-line" onClick={printMerit} disabled={printing}>
              {printing ? "Préparation..." : "Imprimer la liste"}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-foreground-600">
          Toujours affichés : <strong>rang, élève, moyenne et mention</strong>. Cochez les colonnes à ajouter :
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(Object.keys(MERIT_OPTION_LABELS) as Array<keyof MeritListOptions>)
            .filter((key) => key !== "remark" || report?.scope === "PERIOD")
            .map((key) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 rounded-md border border-background-200 px-3 py-2 text-sm hover:bg-background-100">
                <input
                  type="checkbox"
                  checked={meritOptions[key]}
                  onChange={(e) => setMeritOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
                  className="h-4 w-4 accent-primary-600"
                />
                {MERIT_OPTION_LABELS[key]}
              </label>
            ))}
        </div>
      </Modal>

      <Modal
        open={remarkOf !== null}
        onClose={() => setRemarkOf(null)}
        title="Appréciation générale"
        subtitle={remarkOf ? `${remarkOf.lastName.toUpperCase()} ${remarkOf.firstName} — moyenne ${formatScore(remarkOf.average)}` : undefined}
        icon="ri-chat-quote-line"
        footer={
          <>
            <Button variant="outline" onClick={() => setRemarkOf(null)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={submitRemark}>
              Enregistrer
            </Button>
          </>
        }
      >
        <Textarea
          label="Appréciation imprimée sur le bulletin"
          rows={4}
          maxLength={500}
          value={remark}
          placeholder="ex. Bon trimestre, des efforts à poursuivre en mathématiques."
          onChange={(e) => setRemark(e.target.value)}
          hint={`${remark.length} / 500 — laisser vide pour retirer l'appréciation`}
        />
      </Modal>
    </div>
  );
}
